import React from "react";

export default function FormRow({ label, children, className }) {
  // If a `label` prop is provided, use the classic two-column layout.
  if (label) {
    return (
      <div
        className={
          "flex gap-1 items-center py-0.5 text-sm " + (className || "")
        }
      >
        <div className="w-40 min-w-[120px] text-gray-700 font-medium text-sm">
          {label}
        </div>
        <div className="flex-1">{children}</div>
      </div>
    );
  }

  // Support the alternate API where callers pass a single child wrapper that
  // itself contains one or two column blocks (used by `DynamicForm`). Normalize
  // those into consistent label/value grids so single-field rows align with
  // paired rows.
  const parts = React.Children.toArray(children);
  if (parts.length === 1) {
    const child = parts[0];
    const cols =
      child && child.props ? React.Children.toArray(child.props.children) : [];

    if (cols.length === 2) {
      return (
        <div className={"grid grid-cols-2 gap-x-6 " + (className || "")}>
          {cols.map((col, idx) => {
            const inner =
              col && col.props
                ? React.Children.toArray(col.props.children)
                : [];
            const labelEl = inner[0] ?? null;
            const valueEl = inner[1] ?? null;
            return (
              <div
                key={idx}
                className="grid grid-cols-[160px_1fr] gap-1 py-px text-xs"
              >
                <div className="text-gray-600 flex items-start mt-2 whitespace-nowrap">
                  {labelEl}
                </div>
                <div className="text-gray-800">{valueEl ?? null}</div>
              </div>
            );
          })}
        </div>
      );
    }

    if (cols.length === 1) {
      const inner =
        cols[0] && cols[0].props
          ? React.Children.toArray(cols[0].props.children)
          : [];
      if (inner.length >= 2) {
        const labelEl = inner[0];
        const valueEl = inner[1];
        return (
          <div
            className={
              "grid grid-cols-[160px_1fr] gap-1 py-px text-xs " +
              (className || "")
            }
          >
            <div className="text-gray-600 flex items-center whitespace-nowrap">
              {labelEl}
            </div>
            <div className="text-gray-800">{valueEl ?? null}</div>
          </div>
        );
      }
    }

    // Fallback: render child as-is inside the standard flex wrapper
    return (
      <div
        className={
          "flex gap-1 items-center py-0.5 text-sm " + (className || "")
        }
      >
        <div className="w-40 min-w-[120px] text-gray-700 font-medium text-sm"></div>
        <div className="flex-1">{child}</div>
      </div>
    );
  }

  // If multiple children passed, treat first as label and second as value.
  const lbl = parts[0] ?? null;
  const val = parts[1] ?? null;
  return (
    <div
      className={"flex gap-1 items-center py-0.5 text-sm " + (className || "")}
    >
      <div className="w-40 min-w-[120px] text-gray-700 font-medium text-sm">
        {lbl}
      </div>
      <div className="flex-1">{val}</div>
    </div>
  );
}

export function FormSection({ title, children }) {
  return (
    <div className="border-t border-gray-100 pt-2 mt-3">
      {title && (
        <div className="font-medium mb-2 text-sm flex items-center gap-2">
          {title}
        </div>
      )}
      <div className="space-y-1">{children}</div>
    </div>
  );
}
